import { InventoryMutation } from "@domain/inventory/models/InventoryMutation";
import { ApiClient } from "../../ApiClient";
import { ProductInventoryDTO, WarehouseInventoryDTO } from "../dto/InventoryDTO";
import { InventoryServiceLevelDTO } from "../dto/InventoryServiceLevelDTO";
import { InventoryStockLevelsDTO } from "../dto/InventoryStockLevelsDTO";
import { PaginatedInventoryMutationsDTO } from "../dto/InventoryMutationDto";
import { InventoryMutationMapper } from "../mappers/InventoryMutationMapper";
import { PaginatedDTO } from "@infrastructure/paginated.dto";
import { InventoryFilter } from "@domain/inventory/models/InventoryFilter";
import { ProductFilter } from "@domain/product/models/ProductFilter";

export class InventoryDataSource {
    constructor(private api: ApiClient) { }

    async getInventory(filter?: InventoryFilter, active = true): Promise<PaginatedDTO<ProductInventoryDTO[]>> {
        try {
            const response = await this.api.get<PaginatedDTO<ProductInventoryDTO[]>>('/inventory', {
                params: { date: filter?.date?.toISOString(), page: filter?.page, limit: filter?.limit, ...filter, active }
            });

            return response.data;
        } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Failed to fetch inventory');
        }
    }

    async getInventoryByProductId(productId: string): Promise<ProductInventoryDTO> {
        try {
            const response = await this.api.get<ProductInventoryDTO>(`/inventory/product/${productId}`);
            return response.data;
        } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Failed to fetch inventory');
        }
    }

    async getInventoryServiceLevel(filter?: ProductFilter): Promise<InventoryServiceLevelDTO> {
        try {
            const response = await this.api.get<InventoryServiceLevelDTO>('/inventory/service-level', {
                params: { ...filter },
            });
            return response.data;
        } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Failed to fetch inventory service level');
        }
    }

    async getInventoryStockLevels(filter?: ProductFilter): Promise<InventoryStockLevelsDTO> {
        try {
            const response = await this.api.get<InventoryStockLevelsDTO>('/inventory/stock-levels', {
                params: { ...filter },
            });
            return response.data;
        } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Failed to fetch inventory stock levels');
        }
    }

    async getInventoryForWarehouse(warehouseId?: string, filter?: ProductFilter, page?: number, limit?: number): Promise<PaginatedDTO<WarehouseInventoryDTO[]>> {
        try {
            const response = await this.api.get<PaginatedDTO<WarehouseInventoryDTO[]>>(`/inventory/warehouse/${warehouseId}`, {
                params: {
                    ...filter,
                    page,
                    limit
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error instanceof Error ? error.message : 'Failed to fetch inventory');
        }
    }

    async submitManualMutation(mutations: Omit<InventoryMutation, 'id' | 'createdAt'>[], date: Date): Promise<void> {
        try {
            await this.api.post('/inventory/mutations/manual', {
                mutations: mutations.map(mutation => ({
                    warehouseId: mutation.warehouse.id,
                    productId: mutation.product.id,
                    quantity: mutation.quantity,
                    type: mutation.type,
                    date: mutation.date
                })),
                date: date.toISOString()
            });
        } catch (error) {
            throw new Error('Failed to submit inventory mutation');
        }
    }

    async submitFileMutation(file: File, warehouseConnectionId: string): Promise<void> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('warehouseConnectionId', warehouseConnectionId);

            await this.api.post('/inventory/mutations/file', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error) {
            throw new Error('Failed to submit file mutation');
        }
    }


    async getInventoryMutations(warehouseId: string, page: number = 1, limit: number = 20, search?: string): Promise<{ data: InventoryMutation[]; total: number }> {
        try {
            const response = await this.api.get<PaginatedInventoryMutationsDTO>(`/inventory/warehouse/${warehouseId}/mutations`, {
                params: { page, limit, ...(search ? { search } : {}) }
            });
            return {
                data: response.data.data.map(InventoryMutationMapper.toModel),
                total: response.data.total
            };
        } catch (error) {
            throw new Error('Failed to fetch inventory mutations');
        }
    }

    async deleteInventoryMutations(mutationIds: string[]): Promise<void> {
        try {
            await this.api.delete('/inventory/mutations', {
                data: { mutationIds }
            });
        } catch (error) {
            throw new Error('Failed to delete inventory mutations');
        }
    }

}